import dayjs from 'dayjs';

/**
 * Parse a backend value while PRESERVING its intended wall-clock.
 *
 * The backend now localizes every date/time to the recipient's timezone and emits ISO-8601 with
 * that offset (e.g. "2026-06-24T14:00:00+02:00"). The frontend must therefore NOT re-zone to the
 * browser — it just renders what the backend computed. We strip any trailing offset so the embedded
 * wall-clock is rendered verbatim; numeric yyyyMMddHHmmss values (also already localized
 * server-side) are read as a literal wall-clock; Date objects are used as-is.
 */
export function ccmsWallClock(value: string | number | Date): dayjs.Dayjs {
  if (value instanceof Date) return dayjs(value);
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const s = String(value);
    if (s.length === 14) {
      const iso = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}T${s.slice(8, 10)}:${s.slice(10, 12)}:${s.slice(12, 14)}`;
      return dayjs(iso); // naive → rendered as-is, no timezone shift
    }
    return dayjs(Number(value)); // epoch ms fallback
  }
  // String: drop a trailing 'Z' or ±hh:mm offset so the backend-localized wall-clock shows verbatim.
  return dayjs(String(value).replace(/(Z|[+-]\d{2}:?\d{2})$/, ''));
}

export function formatDate(value: string | number | Date, _locale = 'en-US') {
  return ccmsWallClock(value).format('MMM D, YYYY');
}

export function formatDateTime(value: string | number | Date, _locale = 'en-US') {
  return ccmsWallClock(value).format('MMM D, YYYY, hh:mm A');
}

export function formatTime(value: string | number | Date, _locale = 'en-US') {
  return ccmsWallClock(value).format('h:mm A');
}

export function formatRelative(target: string | number | Date, from: string | number | Date = Date.now()) {
  const targetDate = new Date(target);
  const fromDate = new Date(from);
  const diffMs = targetDate.getTime() - fromDate.getTime();

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const divisions: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 1000 * 60 * 60 * 24 * 365],
    ['month', 1000 * 60 * 60 * 24 * 30],
    ['week', 1000 * 60 * 60 * 24 * 7],
    ['day', 1000 * 60 * 60 * 24],
    ['hour', 1000 * 60 * 60],
    ['minute', 1000 * 60],
    ['second', 1000],
  ];

  for (const [unit, ms] of divisions) {
    const amount = diffMs / ms;
    if (Math.abs(amount) >= 1 || unit === 'second') {
      return rtf.format(Math.round(amount), unit);
    }
  }
  return rtf.format(0, 'second');
}

export function formatDuration(ms: number, options: { precision?: 'seconds' | 'minutes' | 'hours' | 'days' } = {}) {
  const { precision = 'seconds' } = options;
  const abs = Math.abs(ms);
  const seconds = Math.floor(abs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const parts: string[] = [];
  if (precision === 'days') {
    parts.push(`${days}d`);
  } else if (precision === 'hours') {
    parts.push(`${hours}h`);
    parts.push(`${minutes % 60}m`);
  } else if (precision === 'minutes') {
    parts.push(`${minutes}m`);
    parts.push(`${seconds % 60}s`);
  } else {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
}

export function timeAgo(value: string | number | Date) {
  return formatRelative(value, Date.now());
}

export function isToday(value: string | number | Date) {
  const date = new Date(value);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function startOfDay(value: string | number | Date) {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(value: string | number | Date) {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function addDays(value: string | number | Date, days: number) {
  const d = new Date(value);
  d.setDate(d.getDate() + days);
  return d;
}

export function diffInDays(a: string | number | Date, b: string | number | Date) {
  const aDate = new Date(a);
  const bDate = new Date(b);
  const diff = startOfDay(aDate).getTime() - startOfDay(bDate).getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

export function parseIso(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toIsoDate(value: string | number | Date) {
  // Use the preserved wall-clock so an offset value isn't pushed to the previous/next UTC day.
  return ccmsWallClock(value).format('YYYY-MM-DD');
}

/**
 * Format a value for SENDING to the backend: ISO-8601 carrying the LOCAL offset
 * (e.g. "2026-06-24T14:00:00+02:00"). The backend converts this to UTC at the boundary, so the
 * instant is unambiguous regardless of the user's timezone. Use this for POST/PUT bodies and
 * date/time filter query params instead of `.toISOString()` (which would send UTC and lose the
 * sender's intent for naive pickers).
 */
export function toApiDateTime(value: string | number | Date): string {
  return dayjs(value).format(); // dayjs default format = ISO-8601 with local offset
}

export function formatRange(start: string | number | Date, end: string | number | Date, locale = 'en-US') {
  return `${formatDate(start, locale)} – ${formatDate(end, locale)}`;
}

export function minutesBetween(a: string | number | Date, b: string | number | Date) {
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return Math.round(diff / (1000 * 60));
}

export function secondsBetween(a: string | number | Date, b: string | number | Date) {
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return Math.round(diff / 1000);
}

export function isSameDay(a: string | number | Date, b: string | number | Date) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}
